/**
 * @file Dialog 模态对话框
 * @description 居中弹层 + 遮罩；支持 Esc / 点遮罩关闭；开关带 fade。
 *
 * @whenToUse 确认操作、表单弹窗、Showcase Prompt 预览。
 * @whenNotToUse
 * 1. 右侧详情编辑（用 Drawer）。
 * 2. 轻提示（用 Toast）。
 */

import {
  useEffect,
  useId,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'

export type DialogProps = {
  /** 是否打开 */
  open: boolean
  /** 打开状态变化 */
  onOpenChange: (open: boolean) => void
  /** 标题 */
  title: string
  /** 主内容 */
  children: ReactNode
  /** 底部操作区 */
  footer?: ReactNode
  /** 内容区最大宽度 class，默认 max-w-lg */
  className?: string
  /** 内容区域自定义类名 */
  contentClassName?: string
}

/**
 * 模态对话框（Portal + fade）
 */
export function Dialog({
  open,
  onOpenChange,
  title,
  children,
  footer,
  className,
  contentClassName
}: DialogProps) {
  const titleId = useId()
  const [mounted, setMounted] = useState(open)
  const [visible, setVisible] = useState(open)

  // 先挂载（opacity-0），再在下一帧打开，避免首帧直接 opacity-100 跳过过渡
  useEffect(() => {
    if (open) {
      setMounted(true)
      return
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!mounted || !open) return
    const id = requestAnimationFrame(() => setVisible(true))
    return () => cancelAnimationFrame(id)
  }, [mounted, open])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onOpenChange(false)
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, onOpenChange])

  if (!mounted || typeof document === 'undefined') return null

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="关闭弹层"
        className={cn(
          'absolute inset-0 bg-foreground/40 backdrop-blur-xs transition-opacity duration-200',
          visible ? 'opacity-100' : 'opacity-0',
        )}
        onClick={() => onOpenChange(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'relative z-10 flex w-full flex-col rounded-2xl border border-border bg-raised transition-opacity duration-200',
          visible ? 'opacity-100' : 'opacity-0',
          className ?? 'max-w-lg',
        )}
      >
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <h2 id={titleId} className="text-lg font-semibold tracking-tight">
            {title}
          </h2>
          <button
            type="button"
            aria-label="关闭"
            className="inline-flex size-8 items-center justify-center rounded-lg border-2 border-border text-muted transition-opacity duration-200 hover:opacity-90 focus-visible:opacity-90"
            onClick={() => onOpenChange(false)}
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
        <ScrollArea className={cn('min-h-0 flex-1 px-4 py-3', contentClassName)}>{children}</ScrollArea>
        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t border-border px-4 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  )
}
